import type { ReactNode } from 'react';
import type { DataTableColumn } from '../types';
import { cx } from '../utils';
import { buildColumn, EMPTY_VALUE, type ColumnPresetOptions } from './base';
import { CellAvatar } from './CellAvatar';

export type AvatarColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** The person's name. Drives the label and the initials fallback. */
    name: (row: T) => string | null | undefined;
    /** Avatar image. Missing or broken URLs fall back to initials. */
    imageUrl?: (row: T) => string | null | undefined;
    /** Optional second line under the name (email, role, practice, …). */
    sublabel?: (row: T) => string | null | undefined;
    /**
     * Replaces the *name text* with caller-owned content, keeping the avatar
     * and the layout. This is the seam for the interactive case - an assignee
     * picker, say. Presentation lives here; anything that mutates data lives in
     * the app, and pairs with `stopClickPropagation`.
     */
    renderLabel?: (row: T, index: number) => ReactNode;
    /** Size/ring overrides for the avatar. Defaults to `'h-8 w-8'`. */
    avatarClassName?: string;
    /** Classes on the name line. */
    labelClassName?: string;
    fallback?: ReactNode;
};

/**
 * Avatar plus name, with an initials fallback.
 *
 * `truncate` defaults **off** on the cell: the text lines truncate
 * individually inside the flex row, so clipping the whole cell would cut the
 * avatar instead of the overflowing text.
 */
export function avatarColumn<T, K extends string = string>(
    options: AvatarColumnOptions<T, K>
): DataTableColumn<T, K> {
    const {
        name,
        imageUrl,
        sublabel,
        renderLabel,
        avatarClassName,
        labelClassName,
        fallback = EMPTY_VALUE
    } = options;

    return buildColumn(
        options,
        (row, index) => {
            const displayName = name(row) ?? '';
            if (!displayName && !renderLabel) {
                return fallback;
            }
            const sub = sublabel?.(row);
            return (
                <div className="flex items-center gap-2 min-w-0">
                    <CellAvatar
                        src={imageUrl?.(row)}
                        name={displayName}
                        className={avatarClassName}
                    />
                    <div className="min-w-0">
                        {renderLabel ? (
                            renderLabel(row, index)
                        ) : (
                            <span className={cx('block truncate', labelClassName)}>
                                {displayName}
                            </span>
                        )}
                        {sub ? (
                            <span className="block truncate text-xs text-gray-500">{sub}</span>
                        ) : null}
                    </div>
                </div>
            );
        },
        {
            truncate: false,
            // No default reveal when the label is caller-owned: `renderLabel`
            // is the interactive seam (assignee picker, …), and a cell-level
            // tooltip must never sit over someone else's control.
            getTitle: renderLabel ? undefined : (row) => name(row) ?? undefined
        }
    );
}
